import React, { FC } from "react";
import {
  Box,
  Grid,
  Typography,
  useTheme,
  useMediaQuery,
  styled,
} from "@mui/material";
import { suits } from "./suitsData";

/* ---------- styled helpers ---------- */

// Section wrapper
const SectionWrapper = styled("section")(({ theme }) => ({
  position: "relative",
  padding: theme.spacing(10, 2, 12),
  maxWidth: 1300,
  margin: "0 auto",
  overflow: "hidden",
    borderRadius: 6,            
  /* ↓ neutral paper tone */
  backgroundColor: "#FFFFFF",
  "&:before": {
    /* optional blur accent (kept) */
    content: "''",
    position: "absolute",
    top: -120,
    left: -120,
    width: 320,
    height: 320,
    background:
      "radial-gradient(circle, rgba(159,3,255,0.12), transparent 70%)",
    filter: "blur(80px)",
    zIndex: 0,
  },
}));


// Glass card
const Card = styled(Box)(({ theme }) => ({
  background: "rgba(255,255,255,0.78)",
  backdropFilter: "saturate(180%) blur(14px)",
  borderRadius: 20,
  padding: theme.spacing(6, 4.5),
  textAlign: "center",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  transition:
    "transform .45s cubic-bezier(.22,1,.36,1), box-shadow .45s, background .45s",
  boxShadow: "0 6px 30px rgba(0,0,0,0.06)",
  "&:hover": {
    transform: "translateY(-10px) scale(1.03)",
    boxShadow: "0 18px 40px rgba(0,0,0,0.12)",
    background: "rgba(255,255,255,0.92)",
  },
}));

// Badge with prop-filter so `gradient` never hits the DOM
const Badge = styled("div", {
  shouldForwardProp: (p) => p !== "gradient",
})<{ gradient: string }>(({ gradient }) => ({
  width: 88,
  height: 88,
  borderRadius: "50%",
  marginBottom: 20,
  background: gradient,
  position: "relative",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
  "&:before": {
    content: "''",
    position: "absolute",
    inset: 0,
    borderRadius: "50%",
    background:
      "radial-gradient(circle at 30% 30%, rgba(255,255,255,0.5), transparent 60%)",
  },
}));

/* ---------- component ---------- */
const FourSuits: FC = () => {
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));

  return (
    <SectionWrapper id="suits">
      {/* Heading */}
      <Typography
        variant={isMdUp ? "h3" : "h4"}
        fontWeight={800}
        textAlign="center"
        borderRadius="6px"
        sx={{ fontFamily: '"Lexend", sans-serif', color: "#2E1065", mb: 1 }}
      >
        Explore the Four Suits
      </Typography>
      <Typography
        variant="body1"
        color="text.secondary"
        sx={{
          maxWidth: 640,
          mx: "auto",
          textAlign: "center",
          mb: { xs: 7, md: 9 },
          fontFamily: '"Lexend", sans-serif',
        }}
      >
        Each suit unlocks a unique form of live interaction—purpose-built
        experiences for entertainment, learning, community, or commerce.
      </Typography>

      {/* Grid of suits */}
      <Grid container spacing={4} sx={{ position: "relative", zIndex: 1 }}>
        {suits.map(({ title, desc, icon, gradient }, idx) => (
          <Grid key={idx} item xs={12} sm={6} md={4} lg={3}>
            <Card role="button" aria-label={title}>
              <Badge gradient={gradient}>{icon}</Badge>

              <Typography
                fontWeight={700}
                sx={{
                  fontSize: 20,
                  color: "#2E1065",
                  mb: 1.2,
                  fontFamily: '"Lexend", sans-serif',
                }}
              >
                {title}
              </Typography>

              <Typography
                color="text.secondary"
                sx={{
                  fontSize: 15,
                  lineHeight: 1.7,
                  fontFamily: '"Lexend", sans-serif',
                }}
              >
                {desc}
              </Typography>
            </Card>
          </Grid>
        ))}
      </Grid>
    </SectionWrapper>
  );
};

export default FourSuits;
